import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Button, Segment, Header, Radio, Loader } from 'semantic-ui-react';
import HelpButton from '../HelpButton';
import { useMapToOptions } from '../utilities';
import { VehicleFields } from './VehicleFields';
import styles from './VehicleForm.module.scss';
import useDebounce from '../../../../../helpers/debounce';
import VehicleState from './VehicleState';
import { Field, useErrors, useForm, useIsValid } from '../PolicyForm';

const VehicleForm = ({ data, defaultGaragingAddress, onChange, onSaveAndExit, onContinue, onCancel, updateVehicle, vehicleState, addressState, onBack, addressLookup }) => {
	const annualMileageOptions = useMapToOptions(VehicleFields.annualMileage.options);
	const yearOptions = useMapToOptions(VehicleFields.year.options, true);
	const [showErrors, setShowErrors] = useState(false);

	const styleOptions = useMemo(
		() =>
			vehicleState.vehicle?.styles?.map((s, i) => {
				return {
					key: i,
					text: s.display,
					value: JSON.stringify(s),
				};
			}) || [],
		[vehicleState]
	);

	useEffect(() => {
		if(!defaultGaragingAddress && !data.isOtherAddress) {
			onChange({ isOtherAddress: true })
		}
	}, [data.isOtherAddress, defaultGaragingAddress, onChange])

	useEffect(() => {
		if (vehicleState.vehicle != null && data.style != null && !styleOptions.find((so) => so.value === data.style)) {
			onChange({ style: null });
		}
	}, [data.style, onChange, styleOptions, vehicleState.vehicle]);

	const stateOptions = useMapToOptions(VehicleFields.garagingAddressStateId.options(), false, null, VehicleFields.garagingAddressStateId.base);
	

	const onSelectedAddressChange = useCallback(
		(addy) => {
			onChange({
				isOtherAddress: addy === null,
				garagingAddressHouseNumber: '',
				garagingAddressStreetName: '',
				garagingAddressApartmentNumber: '',
				garagingAddressPOBox: '',
				garagingAddressCity: '',
				garagingAddressStateId: 0,
				garagingAddressZip: '',
				garagingAddressCounty: '',
			});
		},
		[onChange]
	);
	
	const _onChange = useCallback(
		(_, { name, value }) => {
			let _change = { [name]: value };

			if (name === 'addBy') {
				_change.vin = '';
				_change.year = 0;
				_change.make = '';
				_change.model = '';
				_change.style = '';
				updateVehicle();
                addressLookup();
            }

			onChange(_change);
		},
		[onChange, updateVehicle, addressLookup]
	);

	const debouncedVin = useDebounce(data.vin, 500);
	const debouncedYear = useDebounce(data.year, 500);
	const debouncedMake = useDebounce(data.make, 500);
	const debounceModel = useDebounce(data.model, 500);

	useEffect(() => {
		updateVehicle(debouncedVin, debouncedYear, debouncedMake, debounceModel);
	}, [debounceModel, debouncedMake, debouncedVin, debouncedYear, updateVehicle]);

	const debounceZip = useDebounce(data.garagingAddressZip, 500);
	useEffect(() => {
		addressLookup(debounceZip);
	}, [addressLookup, debounceZip]);

	const formErrors = useErrors(data, VehicleFields);

	const errors = useMemo(() => {
		const errors = { ...formErrors };

		if (data.vin === '' && !(data.style != null && styleOptions.find((so) => so.value === data.style))) errors.style = 'Invalid';
		if (data.vin !== '' && data.vin.length !== 17) errors.vin = 'Invalid';
		if (!vehicleState.valid) errors.vehicleState = 'Invalid';

		return errors;
	}, [data, formErrors, styleOptions, vehicleState.valid]);

	const isValid = useIsValid(errors);
	const form = useForm(VehicleFields, data, errors, showErrors, _onChange);

	const _onContinue = useCallback(() => {
		if (isValid) {
			onContinue();
		} else {
			setShowErrors(true);
		}
	}, [isValid, onContinue]);
	
	return (
		<Form>
			<Segment>
				<Form.Group inline>
					<label>{VehicleFields.addBy.label}</label>
					<Form.Radio id="addByVin" name='addBy' label={VehicleFields.addBy.options[1]} checked={data.addBy} onClick={() => _onChange(null, { name: 'addBy', value: true })} />
					<Form.Radio id="addByYMM" name='addBy' label={VehicleFields.addBy.options[0]} checked={!data.addBy} onClick={() => _onChange(null, { name: 'addBy', value: false })} />
				</Form.Group>

                <Form.Group>
                    {!data.addBy && (
						<React.Fragment>
							<Field as={Form.Dropdown} name='year' options={yearOptions} scrolling selection {...form} />
							<Field name='make' {...form} />
							<Field name='model' {...form} />
						</React.Fragment>
					)}
                    {data.addBy && (
						<Field
							className={styles.fixedWidth}
							name='vin'
							label={
								<label>
									{VehicleFields.vin.label} <HelpButton>{VehicleFields.vin.help}</HelpButton>
								</label>
							}
							inline
							{...form}
						/>
					)}

					<VehicleState state={vehicleState} isVin={data.addBy} />
				</Form.Group>
				{!data.addBy && vehicleState.vehicle && <Field as={Form.Dropdown} name='style' className={styles.fixedWidth} options={styleOptions} selection inline scrolling {...form} />}
				<Field as={Form.Dropdown} className={styles.fixedWidth} name='annualMileage' options={annualMileageOptions} selection inline {...form} />
				<Form.Group className={styles.fixedWidth} inline>
					<label>{VehicleFields.loanOrLease.label}</label>
					<Form.Radio id="loanOrLeaseNo" name='loanOrLease' label='No' checked={!data.loanOrLease} onChange={() => onChange({ loanOrLease: false })} />
					<Form.Radio id="loanOrLeaseYes" name='loanOrLease' label='Yes' checked={data.loanOrLease} onChange={() => onChange({ loanOrLease: true })} />
				</Form.Group>
			</Segment>
			<Segment>
				<Header>Primary Garaged Location</Header>
				{defaultGaragingAddress && (
					<div className={styles.addresses}>
						<div className={styles.address}>
							<div>
								<Radio id="selectedAddressDefault" name='selectedAddress' onClick={() => onSelectedAddressChange(defaultGaragingAddress)} checked={!data.isOtherAddress} />
							</div>
							<div>
								<div>{defaultGaragingAddress.line1}</div>
								<div>{defaultGaragingAddress.line2}</div>
							</div>
						</div>
						<div className={styles.address}>
							<div>
								<Radio id="selectedAddressOther" name='selectedAddress' onClick={() => onSelectedAddressChange(null)} checked={data.isOtherAddress} />
							</div>
							<div>Other</div>
						</div>
					</div>
				)}

                {data.isOtherAddress && (
					<React.Fragment>
						<Form.Group>
							<Field name='garagingAddressHouseNumber' {...form} />
							<Field name='garagingAddressStreetName' {...form} />
						</Form.Group>
						<Form.Group>
							<Field name='garagingAddressApartmentNumber' {...form} />
							<Field name='garagingAddressPOBox' {...form} />
						</Form.Group>
						<Form.Group>
							<Field name='garagingAddressZip' {...form} />
							<Form.Field className={styles.loadingBox}>
                                {addressState.loadingAddress && (
								    <React.Fragment>
									    <Loader active inline />
									    <span className={styles.validating}>Validating zip code</span>
								    </React.Fragment>
								)}
							</Form.Field>
                        </Form.Group>
						<Form.Group>
							<Field name='garagingAddressCity' {...form} />
							<Field as={Form.Dropdown} name='garagingAddressStateId' options={stateOptions} scrolling selection search {...form} />
							<Field name='garagingAddressCounty' {...form}  />
						</Form.Group>
					</React.Fragment>
				)}
            </Segment>

			<div className={styles.buttons}>
				<Button id="continueBtn" primary onClick={_onContinue}>
					Continue
				</Button>
				<Button id="saveAndExitBtn" primary onClick={onSaveAndExit}>
					Save and Exit
				</Button>
				<Button id="cancelBtn" primary onClick={onCancel}>
					Cancel
				</Button>
				<Button id="back" primary onClick={onBack}>
					Back
				</Button>
			</div>
		</Form>
	);
};

VehicleForm.propTypes = {};

export default VehicleForm;
